import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CloseIcon } from "@/assets/icons/general/Close";
import { IconButton } from "@/components/ui/buttons/IconButton/IconButton";
import { Modal } from "@/components/ui/overlays/Modal/Modal";
import { useToast } from "@/components/ui/status/Toast/useToast";
import { Typography } from "@/components/ui/text/Typography/Typography";
import UploadsProfileForm, { type IUploadsProfileFormData } from "@/components/uploads/profile/UploadsProfileForm";
import { BizProfileModels } from "@/data/bizProfile/bizProfile.models";
import { BizProfileQueries } from "@/data/bizProfile/bizProfile.queries";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProfiles?: BizProfileModels.BizProfileListDto[];
  singleProfileEdit?: BizProfileModels.BizProfileListDto;
  onBulkUpdateSuccess?: () => void;
}

const UploadsPageEditProfilesModal = ({
  isOpen,
  onClose,
  selectedProfiles,
  singleProfileEdit,
  onBulkUpdateSuccess,
}: IProps) => {
  const [profilesToEdit, setProfilesToEdit] = useState<BizProfileModels.BizProfileListDto[]>([]);
  const { successToast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: bulkUpdateProfile } = BizProfileQueries.useBulkUpdate({
    onSuccess: (response) => {
      // Optimistically update all profile list queries to show the processing state
      queryClient.setQueriesData({ queryKey: [...BizProfileQueries.keys.all] }, (old: any) => {
        if (!old?.items) return old;

        return produce(old, (draft: any) => {
          draft.items.forEach((item: any) => {
            if (response.ids.includes(item.id)) {
              // oxlint-disable-next-line no-param-reassign
              item.status = BizProfileModels.BizProfileListStatusEnum.processing;
            }
          });
        });
      });

      // Clear row selection after successful bulk update
      onBulkUpdateSuccess?.();
    },
  });

  useEffect(() => {
    if (singleProfileEdit) {
      setProfilesToEdit([singleProfileEdit]);
      return;
    }
    // Filter out processing_failed rows - only allow processed rows for Edit
    const processedProfilesOnly =
      selectedProfiles?.filter((profile) => profile.status === BizProfileModels.BizProfileListStatusEnum.processed) ||
      [];
    setProfilesToEdit(processedProfilesOnly);
  }, [selectedProfiles, singleProfileEdit]);

  const { t } = useTranslation();

  const handleSaveAndClose = async (data: IUploadsProfileFormData) => {
    bulkUpdateProfile({
      data: {
        ids: profilesToEdit.map((profile) => profile.id),
        tags: data.tags.map((tag) => tag.name),
        jobTitle: data.jobPosition,
        location: data.location,
      },
    });

    successToast({
      text: singleProfileEdit
        ? t("uploads.userUploads.update-success")
        : t("uploads.userUploads.update-multiple-success"),
    });

    onClose();
  };

  const removeProfile = (profile: BizProfileModels.BizProfileListDto) => {
    setProfilesToEdit(profilesToEdit?.filter((p) => p.id !== profile.id));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      title={t("uploads.files-upload-modal.title")}
      modalClassName="!max-h-[80vh] !max-w-[555px]"
    >
      <UploadsProfileForm
        onConfirm={handleSaveAndClose}
        subtitle={t("uploads.files-upload-modal.add-info-text")}
        jobTitle={singleProfileEdit?.jobTitle || undefined}
        metroArea={singleProfileEdit?.location || undefined}
        tags={singleProfileEdit?.tags || undefined}
      >
        <div className="flex max-h-[300px] w-full flex-1 flex-col overflow-y-auto">
          {profilesToEdit?.map((profile, index) => (
            <div
              key={index}
              className="flex flex-row items-center justify-between border-elevation-outline-1 border-b px-3 py-2-5"
            >
              <div className="flex flex-col gap-1">
                <Typography size="body-4">{profile.name}</Typography>
                <div className="flex flex-row flex-wrap gap-2">
                  {profile?.tags?.map((tag) => (
                    <div
                      key={tag}
                      className="flex gap-1-5 rounded-button-rounding bg-elevation-surface-1 px-1-5 py-1"
                    >
                      <Typography size="label-5">{tag}</Typography>
                    </div>
                  ))}
                </div>
              </div>

              {!singleProfileEdit && (
                <IconButton
                  variant="transparent"
                  icon={CloseIcon}
                  iconClassName="h-6 w-6 text-text-default-primary"
                  label=""
                  onPress={() => removeProfile(profile)}
                />
              )}
            </div>
          ))}
        </div>
      </UploadsProfileForm>
    </Modal>
  );
};

export default UploadsPageEditProfilesModal;
